import { useCallback, useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Trash } from "@phosphor-icons/react";

const ROLES = ["user", "admin"];
const PLANS = ["Starter", "Professional", "Enterprise"];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);

  const load = useCallback(() => client.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const setRole = async (uid, role) => {
    await client.post(`/admin/users/${uid}/role`, { role });
    toast.success(`Role updated`);
    load();
  };

  const setPlan = async (uid, plan) => {
    await client.post(`/admin/users/${uid}/plan`, { plan });
    toast.success(`Plan updated`);
    load();
  };

  const remove = async (uid) => {
    if (!window.confirm("Delete this user? This cannot be undone.")) return;
    try {
      await client.delete(`/admin/users/${uid}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#64748B] mb-1">Admin · Users</div>
      <h1 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#0F172A] mb-6">
        User management
      </h1>

      <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F1F5F9] text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Plan</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Joined</th>
              <th className="px-4 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={6} className="text-center text-[#64748B] py-10">No users.</td></tr>
            )}
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[#E5E7EB]" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-semibold">{u.name}</td>
                <td className="px-4 py-3 hidden md:table-cell text-[#475569]">{u.email}</td>
                <td className="px-4 py-3">
                  <select value={u.role || "user"} onChange={(e) => setRole(u.id, e.target.value)} className="trent-input py-1" data-testid={`user-role-${u.id}`}>
                    {ROLES.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <select value={u.plan || "Starter"} onChange={(e) => setPlan(u.id, e.target.value)} className="trent-input py-1" data-testid={`user-plan-${u.id}`}>
                    {PLANS.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-[#64748B] text-xs">
                  {u.created_at?.slice(0, 10)}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => remove(u.id)} className="p-2 hover:bg-[#FEE2E2] rounded-sm text-[#991B1B]" data-testid={`user-delete-${u.id}`}>
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
